import { Swiper } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Thumbs } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

/**
 * BCE Slider Module
 * 
 * Provides Swiper-based image galleries with navigation and thumbnails.
 * Supports automatic initialization via bce_slider CSS class.
 * 
 * @class BceSlider
 */
class BceSlider {
    constructor() {
        this.instances = new WeakMap();
        this.autoInit = false;
        this.defaultSelector = '.bce_slider';
    }

    init(selector = this.defaultSelector) {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
            if (!this.instances.has(element)) {
                this.create(element);
            }
        });
    }

    /**
     * Create a slider instance on a DOM element
     * 
     * @param {HTMLElement} element - The container element with bce_slider class
     * @param {Object} options - Custom Swiper options to override defaults
     * @returns {Object} Object containing main and thumbs Swiper instances
     */
    create(element, options = {}) {
        // Check if element already has a slider instance
        if (this.instances.has(element)) {
            return this.instances.get(element);
        }

        const gallery = this.getGalleryElement(element);

        if (!gallery) {
            console.warn('No galleries found in slider element');
            return;
        }

        // Check if gallery already has swiper classes
        if (gallery.classList.contains('swiper')) {
            return;
        }

        const uniqueId = `slider-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        if (!this.setupSwiperStructure(gallery, uniqueId)) {
            return;
        }

        const swiperInstances = this.initializeSwiper(gallery, uniqueId, options);
        this.instances.set(element, swiperInstances);

        return swiperInstances;
    }

    getGalleryElement(element) {
        return element.querySelector('.ce-gallery, .gallery[data-images], .gallery');
    }

    getSlideElements(gallery) {
        const legacyColumns = gallery.querySelectorAll('.ce-column');
        if (legacyColumns.length > 0) {
            return Array.from(legacyColumns);
        }

        return Array.from(gallery.querySelectorAll(':scope > figure.image'));
    }

    setupSwiperStructure(gallery, uniqueId) {
        const swiperWrapper = document.createElement('div');
        swiperWrapper.classList.add('swiper-wrapper');

        const slides = this.getSlideElements(gallery);

        if (slides.length === 0) {
            console.warn('No slides found in slider gallery');
            return false;
        }

        slides.forEach(slide => {
            slide.classList.add('swiper-slide');
            swiperWrapper.appendChild(slide);
        });

        // Setup main slider
        gallery.innerHTML = '';
        gallery.classList.add('swiper', `main-slider-${uniqueId}`);
        gallery.appendChild(swiperWrapper);

        // Add navigation buttons with unique IDs
        const prevBtn = document.createElement('div');
        prevBtn.classList.add('swiper-button-prev');
        prevBtn.id = `prev-${uniqueId}`;
        
        const nextBtn = document.createElement('div');
        nextBtn.classList.add('swiper-button-next');
        nextBtn.id = `next-${uniqueId}`;
        
        gallery.appendChild(prevBtn);
        gallery.appendChild(nextBtn);

        // Create thumbs slider using original slides
        if (slides.length > 1) {
            const thumbsContainer = document.createElement('div');
            thumbsContainer.classList.add('swiper', `thumbs-slider-${uniqueId}`, 'bce-thumbs-slider');

            const thumbsWrapper = document.createElement('div');
            thumbsWrapper.classList.add('swiper-wrapper');

            slides.forEach(slide => {
                const thumbSlide = document.createElement('div');
                thumbSlide.classList.add('swiper-slide');
                
                const img = slide.querySelector('img');
                if (img) {
                    const clonedImg = img.cloneNode(true);
                    clonedImg.style.width = '100%';
                    clonedImg.style.height = 'auto';
                    thumbSlide.appendChild(clonedImg);
                }
                
                thumbsWrapper.appendChild(thumbSlide);
            });

            thumbsContainer.appendChild(thumbsWrapper);
            gallery.insertAdjacentElement('afterend', thumbsContainer);
        }

        return true;
    }

    initializeSwiper(gallery, uniqueId, customOptions = {}) {
        const defaultOptions = {
            main: {
                loop: true,
                slidesPerView: 1,
                spaceBetween: 10,
                navigation: {
                    nextEl: `#next-${uniqueId}`,
                    prevEl: `#prev-${uniqueId}`,
                }
            },
            thumbs: {
                spaceBetween: 10,
                slidesPerView: 4,
                watchSlidesProgress: true,
                breakpoints: {
                    640: {
                        slidesPerView: 2,
                    },
                    768: {
                        slidesPerView: 3,
                    },
                    1024: {
                        slidesPerView: 4,
                    },
                }
            }
        };

        // Merge custom options
        const mainOptions = { ...defaultOptions.main, ...customOptions.main };
        const thumbsOptions = { ...defaultOptions.thumbs, ...customOptions.thumbs };

        // Initialize thumbs slider first (if exists)
        let thumbsSwiper = null;
        const thumbsElement = document.querySelector(`.thumbs-slider-${uniqueId}`);
        if (thumbsElement) {
            thumbsSwiper = new Swiper(`.thumbs-slider-${uniqueId}`, {
                ...thumbsOptions,
                modules: [Thumbs]
            });
        }

        // Add thumbs connection to main options
        if (thumbsSwiper) {
            mainOptions.thumbs = {
                swiper: thumbsSwiper,
            };
        }

        // Initialize main slider
        const mainSwiper = new Swiper(`.main-slider-${uniqueId}`, {
            ...mainOptions,
            modules: [Navigation, Thumbs]
        });

        // Setup interactions
        if (thumbsSwiper) {
            thumbsSwiper.on('click', function (swiper) {
                const clickedIndex = swiper.clickedIndex;
                if (clickedIndex !== undefined) {
                    mainSwiper.slideToLoop(clickedIndex);
                }
            });

            mainSwiper.on('slideChange', function () {
                thumbsSwiper.slideToLoop(mainSwiper.realIndex);
            });
        }

        return {
            main: mainSwiper,
            thumbs: thumbsSwiper
        };
    }

    destroy(element) {
        const instances = this.instances.get(element);
        if (instances) {
            if (instances.main) instances.main.destroy(true, true);
            if (instances.thumbs) instances.thumbs.destroy(true, true);
            this.instances.delete(element);
        }
    }


    // Method to get instance for external access
    getInstance(element) {
        return this.instances.get(element);
    }
}

export default new BceSlider();
